import React, { useState, useEffect } from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Card, Button, Form, Badge, Spinner, Alert } from 'react-bootstrap';
import { FiBell, FiCheck, FiTrash2, FiFilter, FiRefreshCw, FiSettings, FiMessageSquare } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import TakeActionDialog from './TakeActionDialog';
import './NotificationCenter.css';

const NotificationCenter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    notifications,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    resolveNotification,
    getTypeIcon,
    getTypeColor,
    formatDate
  } = useNotifications();

  const [filter, setFilter] = useState({
    type: 'all',
    status: 'active', // Show only active (unread) notifications by default
    dateRange: 'all'
  });

  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showTakeActionDialog, setShowTakeActionDialog] = useState(false);
  const [selectedNotificationForAction, setSelectedNotificationForAction] = useState(null);

  // Notifications are automatically fetched by NotificationContext when userData is available
  // We don't need to fetch them here again on mount

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchNotifications();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleFilterChange = (key, value) => {
    setFilter(prev => ({ ...prev, [key]: value }));
  };

  const handleSelectNotification = (id) => {
    setSelectedNotifications(prev =>
      prev.includes(id) ? prev.filter(n => n !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications.map(n => n.id));
    }
  };

  const handleBulkAction = async (action) => {
    for (const id of selectedNotifications) {
      if (action === 'read') {
        await markAsRead(id);
      } else if (action === 'delete') {
        await deleteNotification(id);
      }
    }
    setSelectedNotifications([]);
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);

    switch(notification.type) {
      case 'tasks':
        navigate('/tasks');
        break;
      case 'problems':
        navigate('/problems');
        break;
      case 'inventory':
        navigate('/inventory');
        break;
      default:
        break;
    }
  };

  // Apply filters
  const filteredNotifications = notifications.filter(n => {
    // Type filter
    if (filter.type !== 'all' && n.type !== filter.type) return false;

    // Status filter
    if (filter.status !== 'all' && n.status !== filter.status) return false;

    // Date range filter
    if (filter.dateRange !== 'all') {
      const date = new Date(n.reportedDate);
      const now = new Date();
      const daysDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      switch(filter.dateRange) {
        case 'today':
          if (daysDiff !== 0) return false;
          break;
        case 'week':
          if (daysDiff > 7) return false;
          break;
        case 'month':
          if (daysDiff > 30) return false;
          break;
      }
    }

    return true;
  });

  // Group notifications by date
  const groupedNotifications = filteredNotifications.reduce((groups, notification) => {
    const date = new Date(notification.reportedDate).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(notification);
    return groups;
  }, {});

  // Log filtered notifications for debugging
  useEffect(() => {
    console.log('[NotificationCenter] Filtered notifications:', {
      total: notifications.length,
      filtered: filteredNotifications.length,
      activeCount: filteredNotifications.filter(n => n.status === 'active').length,
      currentFilter: filter,
      sample: filteredNotifications.slice(0, 3)
    });
  }, [filteredNotifications.length, filter, notifications.length]);

  return (
    <Container fluid className="notification-center">
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-white py-3">
              <Row className="align-items-center">
                <Col xs={12} md={6}>
                  <div className="d-flex align-items-center">
                    <FiBell className="me-2" size={24} />
                    <h4 className="mb-0">{t('notifications.center')}</h4>
                    <Badge bg="primary" className="ms-2">
                      {filteredNotifications.filter(n => n.status === 'active').length}
                    </Badge>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="d-flex justify-content-end gap-2 mt-3 mt-md-0">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => navigate('/settings#notifications')}
                    >
                      <FiSettings className="me-1" />
                      {t('notifications.settings')}
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                    >
                      <FiRefreshCw className={`me-1 ${isRefreshing ? 'spin' : ''}`} />
                      {t('common.refresh')}
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={markAllAsRead}
                      disabled={!filteredNotifications.some(n => n.status === 'active')}
                    >
                      <FiCheck className="me-1" />
                      {t('notifications.markAllAsRead')}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              {/* Filters */}
              <Row className="mb-4">
                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label className="small text-muted">
                      <FiFilter className="me-1" />
                      {t('notifications.filterByType')}
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={filter.type}
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                    >
                      <option value="all">{t('common.all')}</option>
                      <option value="tasks">{t('notifications.types.tasks')}</option>
                      <option value="problems">{t('notifications.types.problems')}</option>
                      <option value="inventory">{t('notifications.types.inventory')}</option>
                      <option value="weather">{t('notifications.types.weather')}</option>
                      <option value="general">{t('notifications.types.general')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label className="small text-muted">
                      {t('notifications.filterByStatus')}
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={filter.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="all">{t('common.all')}</option>
                      <option value="active">{t('notifications.status.unread')}</option>
                      <option value="dismissed">{t('notifications.status.read')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label className="small text-muted">
                      {t('notifications.filterByDate')}
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={filter.dateRange}
                      onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                    >
                      <option value="all">{t('common.all')}</option>
                      <option value="today">{t('common.today')}</option>
                      <option value="week">{t('common.thisWeek')}</option>
                      <option value="month">{t('common.thisMonth')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                  {selectedNotifications.length > 0 && (
                    <Form.Group>
                      <Form.Label className="small text-muted">
                        {t('notifications.bulkActions')}
                      </Form.Label>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleBulkAction('read')}
                        >
                          <FiCheck size={14} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleBulkAction('delete')}
                        >
                          <FiTrash2 size={14} />
                        </Button>
                      </div>
                    </Form.Group>
                  )}
                </Col>
              </Row>

              {/* Notifications List */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">{t('common.loading')}</p>
                </div>
              ) : error ? (
                <Alert variant="danger">
                  {t('notifications.errorLoading')}: {error}
                </Alert>
              ) : filteredNotifications.length === 0 ? (
                <div className="text-center py-5">
                  <FiBell size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">{t('notifications.noNotifications')}</h5>
                  <p className="text-muted small">
                    {filter.type !== 'all' || filter.status !== 'all' || filter.dateRange !== 'all'
                      ? t('notifications.noMatchingNotifications')
                      : t('notifications.noNotificationsDesc')}
                  </p>
                </div>
              ) : (
                <div className="notification-groups">
                  {selectedNotifications.length > 0 && (
                    <div className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label={t('notifications.selectAll')}
                        checked={selectedNotifications.length === filteredNotifications.length}
                        onChange={handleSelectAll}
                      />
                    </div>
                  )}

                  {Object.entries(groupedNotifications).map(([date, dateNotifications]) => (
                    <div key={date} className="notification-date-group mb-4">
                      <h6 className="notification-date-header text-muted mb-3">
                        {date === new Date().toDateString() ? t('common.today') : date}
                      </h6>
                      {dateNotifications.map(notification => (
                        <div
                          key={notification.id}
                          className={`notification-card ${notification.status === 'active' ? 'unread' : ''} ${
                            selectedNotifications.includes(notification.id) ? 'selected' : ''
                          }`}
                        >
                          <div className="notification-card-select">
                            <Form.Check
                              type="checkbox"
                              checked={selectedNotifications.includes(notification.id)}
                              onChange={() => handleSelectNotification(notification.id)}
                            />
                          </div>
                          <div
                            className="notification-card-icon"
                            style={{
                              backgroundColor: getTypeColor(notification.type) + '20',
                              color: getTypeColor(notification.type)
                            }}
                          >
                            <span className="notification-type-icon">
                              {getTypeIcon(notification.type)}
                            </span>
                          </div>
                          <div
                            className="notification-card-content"
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="notification-card-header">
                              <h6 className="notification-card-title">
                                {notification.title || t('notifications.noTitle')}
                              </h6>
                              {notification.severity && (
                                <Badge
                                  bg={notification.severity === 'high' ? 'danger' :
                                      notification.severity === 'medium' ? 'warning' : 'info'}
                                  size="sm"
                                >
                                  {t(`notifications.severity.${notification.severity}`)}
                                </Badge>
                              )}
                            </div>
                            <p className="notification-card-description">
                              {notification.description || t('notifications.noDescription')}
                            </p>
                            <div className="notification-card-footer">
                              <span className="notification-card-time">
                                {formatDate(notification.reportedDate)}
                              </span>
                              {notification.employee && notification.employee.username && (
                                <span className="notification-card-employee">
                                  👤 {notification.employee.username}
                                </span>
                              )}
                              {notification.span && notification.span.nameEn && (
                                <span className="notification-card-span">
                                  📍 {notification.span.nameEn}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="notification-card-actions">
                            {notification.status === 'active' && (
                              <>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedNotificationForAction(notification);
                                    setShowTakeActionDialog(true);
                                  }}
                                  title={t('notifications.takeAction')}
                                >
                                  <FiMessageSquare />
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => markAsRead(notification.id)}
                                  title={t('notifications.markAsRead')}
                                >
                                  <FiCheck />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => deleteNotification(notification.id)}
                              title={t('notifications.delete')}
                            >
                              <FiTrash2 />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Take Action Dialog */}
      <TakeActionDialog
        show={showTakeActionDialog}
        onHide={() => {
          setShowTakeActionDialog(false);
          setSelectedNotificationForAction(null);
        }}
        notification={selectedNotificationForAction}
        onResolve={async (notification, resolutionDetails) => {
          await resolveNotification(notification.id, resolutionDetails);
          setShowTakeActionDialog(false);
          setSelectedNotificationForAction(null);
        }}
        onDismiss={async (notification) => {
          await markAsRead(notification.id);
          setShowTakeActionDialog(false);
          setSelectedNotificationForAction(null);
        }}
      />
    </Container>
  );
};

export default NotificationCenter;